
import React, { useState, useRef, useCallback, useEffect, useReducer } from 'react'
import { CardContent } from '@/components/ui/card'
import { Icon } from '@/components/ui/icon'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { NodeContainer } from '../base/NodeContainer'
import { NODE_DIMENSIONS } from '@genieartinc/schemas'
import { withNodeOptimization } from '../hoc/withNodeOptimization'

export type DrawingTool = 'brush' | 'eraser' | null

const DEFAULT_BRUSH_SIZE = 20
const MIN_BRUSH_SIZE = 2
const MAX_BRUSH_SIZE = 200
const MAX_UNDO_SNAPSHOTS = 20
const DEFAULT_MASK_OPACITY = 0.55

const MASK_EXPORT_COLOR = '#FFFFFF'

// ---------- undo reducer (atomic stack + index) ----------------------------
interface UndoState {
  stack: ImageData[]
  index: number
}

type UndoAction =
  | { type: 'push'; snap: ImageData }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'reset'; snap: ImageData }

function undoReducer(state: UndoState, action: UndoAction): UndoState {
  switch (action.type) {
    case 'push': {
      const trimmed = state.stack.slice(0, state.index + 1)
      const next = [...trimmed, action.snap]
      if (next.length > MAX_UNDO_SNAPSHOTS) next.shift()
      return { stack: next, index: next.length - 1 }
    }
    case 'undo':
      return state.index > 0 ? { ...state, index: state.index - 1 } : state
    case 'redo':
      return state.index < state.stack.length - 1 ? { ...state, index: state.index + 1 } : state
    case 'reset':
      return { stack: [action.snap], index: 0 }
  }
}

// ---------- component ------------------------------------------------------
export interface MaskNodeProps {
  id?: string
  name?: string
  selected?: boolean
  imageSrc?: string | null
  nodeType?: string
  state?: Record<string, unknown>
  onStateChange?: (
    updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>),
  ) => void
  onSelect?: () => void
  onHoverChange?: (hovered: boolean) => void
  onFileUpload?: (file: File) => void
  onGenerate?: () => void
  onMaskExport?: (maskDataUrl: string) => void
  className?: string
}

function MaskNodeComponent({
  id,
  name = 'Mask Node',
  selected = false,
  imageSrc,
  nodeType = 'image',
  state = {},
  onStateChange,
  onSelect,
  onHoverChange,
  onFileUpload,
  onGenerate,
  onMaskExport,
  className,
}: MaskNodeProps) {
  const [activeTool, setActiveTool] = useState<DrawingTool>('brush')
  const [brushSize, setBrushSize] = useState(DEFAULT_BRUSH_SIZE)
  const [maskOpacity, setMaskOpacity] = useState(DEFAULT_MASK_OPACITY)
  const [showMaskOnly, setShowMaskOnly] = useState(false)
  const [undo, dispatchUndo] = useReducer(undoReducer, { stack: [], index: -1 })
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(null)
  const [sourceImageDims, setSourceImageDims] = useState<{ w: number; h: number } | null>(null)

  const displayCanvasRef = useRef<HTMLCanvasElement>(null)
  // Offscreen mask canvas — never in DOM, avoids overflow/display issues
  const maskCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'))
  const containerRef = useRef<HTMLDivElement>(null)
  const isDrawingRef = useRef(false)
  const lastPointRef = useRef<{ x: number; y: number } | null>(null)
  const exportTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dprRef = useRef(window.devicePixelRatio || 1)

  const canUndo = undo.index > 0
  const canRedo = undo.index < undo.stack.length - 1
  const width = NODE_DIMENSIONS.MaskNode?.width ?? 500
  const isDrawingTool = activeTool === 'brush' || activeTool === 'eraser'

  // Toggle tool: click active tool to deselect (returns to pointer/idle)
  const toggleTool = useCallback((tool: 'brush' | 'eraser') => {
    setActiveTool(prev => (prev === tool ? null : tool))
  }, [])

  // ---------- cleanup export timer on unmount ------------------------------
  useEffect(() => {
    return () => {
      if (exportTimerRef.current) clearTimeout(exportTimerRef.current)
    }
  }, [])

  // ---------- coordinate helpers -------------------------------------------
  // Convert mouse event to canvas bitmap coordinates (for drawing)
  const getCanvasPoint = useCallback((e: React.MouseEvent | MouseEvent) => {
    const canvas = displayCanvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    // Canvas bitmap = CSS size * DPR, so scale from CSS → bitmap
    const dpr = dprRef.current
    return {
      x: ((e.clientX - rect.left) / rect.width) * (rect.width * dpr),
      y: ((e.clientY - rect.top) / rect.height) * (rect.height * dpr),
    }
  }, [])

  // Convert mouse event to CSS-space point (for cursor div positioning)
  const getCSSPoint = useCallback((e: React.MouseEvent | MouseEvent) => {
    const canvas = displayCanvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }
  }, [])

  const displayToMask = useCallback(
    (dx: number, dy: number) => {
      const display = displayCanvasRef.current
      const mask = maskCanvasRef.current
      if (!display || !mask) return { x: dx, y: dy }
      return {
        x: dx * (mask.width / display.width),
        y: dy * (mask.height / display.height),
      }
    },
    [],
  )

  const getMaskBrushSize = useCallback(() => {
    const display = displayCanvasRef.current
    const mask = maskCanvasRef.current
    if (!display || !mask) return brushSize
    return brushSize * dprRef.current * (mask.width / display.width)
  }, [brushSize])

  // ---------- redraw display canvas from mask ------------------------------
  const redrawDisplay = useCallback(() => {
    const display = displayCanvasRef.current
    const mask = maskCanvasRef.current
    if (!display || !mask) return
    const ctx = display.getContext('2d')
    if (!ctx) return

    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0) // Reset to identity for full-bitmap operations
    ctx.clearRect(0, 0, display.width, display.height)

    if (showMaskOnly) {
      ctx.fillStyle = '#000000'
      ctx.fillRect(0, 0, display.width, display.height)
      ctx.drawImage(mask, 0, 0, display.width, display.height)
    } else {
      ctx.drawImage(mask, 0, 0, display.width, display.height)
      ctx.globalCompositeOperation = 'source-in'
      ctx.fillStyle = `rgba(0, 255, 50, ${maskOpacity})`
      ctx.fillRect(0, 0, display.width, display.height)
      ctx.globalCompositeOperation = 'source-over'
    }
    ctx.restore()
  }, [showMaskOnly, maskOpacity])

  // Redraw when opacity or mask-only mode changes
  useEffect(() => {
    redrawDisplay()
  }, [redrawDisplay])

  // ---------- snapshot helpers ---------------------------------------------
  const pushSnapshot = useCallback(() => {
    const mask = maskCanvasRef.current
    if (!mask) return
    const ctx = mask.getContext('2d')
    if (!ctx) return
    const snap = ctx.getImageData(0, 0, mask.width, mask.height)
    dispatchUndo({ type: 'push', snap })
  }, [])

  const handleUndo = useCallback(() => {
    if (!canUndo) return
    const newIdx = undo.index - 1
    const ctx = maskCanvasRef.current?.getContext('2d')
    if (ctx && undo.stack[newIdx]) {
      ctx.putImageData(undo.stack[newIdx], 0, 0)
      dispatchUndo({ type: 'undo' })
      redrawDisplay()
    }
  }, [canUndo, undo.index, undo.stack, redrawDisplay])

  const handleRedo = useCallback(() => {
    if (!canRedo) return
    const newIdx = undo.index + 1
    const ctx = maskCanvasRef.current?.getContext('2d')
    if (ctx && undo.stack[newIdx]) {
      ctx.putImageData(undo.stack[newIdx], 0, 0)
      dispatchUndo({ type: 'redo' })
      redrawDisplay()
    }
  }, [canRedo, undo.index, undo.stack, redrawDisplay])

  // ---------- mask export (debounced) --------------------------------------
  const scheduleMaskExport = useCallback(() => {
    if (exportTimerRef.current) clearTimeout(exportTimerRef.current)
    exportTimerRef.current = setTimeout(() => {
      const mask = maskCanvasRef.current
      if (!mask) return
      const dataUrl = mask.toDataURL('image/png')
      onMaskExport?.(dataUrl)
    }, 500)
  }, [onMaskExport])

  // ---------- image loading & canvas sizing --------------------------------
  useEffect(() => {
    if (!imageSrc) {
      setSourceImageDims(null)
      return
    }

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const dims = { w: img.naturalWidth, h: img.naturalHeight }
      setSourceImageDims(dims)

      const mask = maskCanvasRef.current
      mask.width = dims.w
      mask.height = dims.h

      const ctx = mask.getContext('2d')
      if (ctx) {
        const snap = ctx.getImageData(0, 0, mask.width, mask.height)
        dispatchUndo({ type: 'reset', snap })
      }
    }
    img.src = imageSrc
    return () => { img.onload = null }
  }, [imageSrc])

  // ---------- ResizeObserver for display canvas sizing (DPR-aware) ----------
  useEffect(() => {
    const container = containerRef.current
    const display = displayCanvasRef.current
    if (!container || !display || !sourceImageDims) return

    const observer = new ResizeObserver(([entry]) => {
      const cssW = entry.contentRect.width
      if (cssW > 0 && sourceImageDims) {
        const dpr = window.devicePixelRatio || 1
        dprRef.current = dpr
        const cssH = cssW * (sourceImageDims.h / sourceImageDims.w)

        // Set bitmap size to CSS size * DPR for sharp rendering
        display.width = cssW * dpr
        display.height = cssH * dpr

        // Scale context so drawing in CSS-pixel units maps to DPR bitmap
        const ctx = display.getContext('2d')
        if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

        redrawDisplay()
      }
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [sourceImageDims, redrawDisplay])

  // ---------- mask restoration (separate from image load) ------------------
  useEffect(() => {
    const maskData = state?.maskData as string | undefined
    const mask = maskCanvasRef.current
    if (!maskData || !mask || !sourceImageDims) return

    const restoreImg = new Image()
    restoreImg.onload = () => {
      const ctx = mask.getContext('2d')
      if (ctx) {
        ctx.drawImage(restoreImg, 0, 0, mask.width, mask.height)
        redrawDisplay()
        const snap = ctx.getImageData(0, 0, mask.width, mask.height)
        dispatchUndo({ type: 'reset', snap })
      }
    }
    restoreImg.src = maskData
    return () => { restoreImg.onload = null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceImageDims])

  // ---------- keyboard shortcuts -------------------------------------------
  useEffect(() => {
    if (!selected) return
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault()
        if (e.shiftKey) handleRedo()
        else handleUndo()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [selected, handleUndo, handleRedo])

  // ---------- prevent React Flow zoom on canvas ----------------------------
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const handler = (e: WheelEvent) => {
      if (!imageSrc || !isDrawingTool) return
      e.preventDefault()
      e.stopPropagation()
    }
    container.addEventListener('wheel', handler, { passive: false })
    return () => container.removeEventListener('wheel', handler)
  }, [imageSrc, isDrawingTool])

  // ---------- drawing engine -----------------------------------------------
  const drawStroke = useCallback(
    (from: { x: number; y: number }, to: { x: number; y: number }) => {
      const displayCtx = displayCanvasRef.current?.getContext('2d')
      const maskCtx = maskCanvasRef.current?.getContext('2d')
      if (!displayCtx || !maskCtx) return

      const isEraser = activeTool === 'eraser'
      const composite: GlobalCompositeOperation = isEraser ? 'destination-out' : 'source-over'
      const dpr = dprRef.current
      const scaledBrush = brushSize * dpr

      // Draw on display canvas (DPR-scaled bitmap)
      displayCtx.save()
      displayCtx.setTransform(1, 0, 0, 1, 0, 0) // Draw in raw bitmap coords
      displayCtx.globalCompositeOperation = composite
      if (showMaskOnly) {
        displayCtx.strokeStyle = isEraser ? 'rgba(255,255,255,1)' : '#FFFFFF'
      } else {
        displayCtx.strokeStyle = isEraser ? 'rgba(255,255,255,1)' : `rgba(0, 255, 50, ${maskOpacity})`
      }
      displayCtx.lineWidth = scaledBrush
      displayCtx.lineCap = 'round'
      displayCtx.lineJoin = 'round'
      displayCtx.beginPath()
      displayCtx.moveTo(from.x, from.y)
      displayCtx.lineTo(to.x, to.y)
      displayCtx.stroke()
      displayCtx.restore()

      // Draw on mask canvas (source image resolution)
      const maskFrom = displayToMask(from.x, from.y)
      const maskTo = displayToMask(to.x, to.y)
      const maskBrush = getMaskBrushSize()

      maskCtx.save()
      maskCtx.globalCompositeOperation = composite
      maskCtx.strokeStyle = isEraser ? 'rgba(255,255,255,1)' : MASK_EXPORT_COLOR
      maskCtx.lineWidth = maskBrush
      maskCtx.lineCap = 'round'
      maskCtx.lineJoin = 'round'
      maskCtx.beginPath()
      maskCtx.moveTo(maskFrom.x, maskFrom.y)
      maskCtx.lineTo(maskTo.x, maskTo.y)
      maskCtx.stroke()
      maskCtx.restore()
    },
    [activeTool, brushSize, maskOpacity, showMaskOnly, displayToMask, getMaskBrushSize],
  )

  const drawDot = useCallback(
    (pt: { x: number; y: number }) => {
      const displayCtx = displayCanvasRef.current?.getContext('2d')
      const maskCtx = maskCanvasRef.current?.getContext('2d')
      if (!displayCtx || !maskCtx) return

      const isEraser = activeTool === 'eraser'
      const composite: GlobalCompositeOperation = isEraser ? 'destination-out' : 'source-over'
      const dpr = dprRef.current
      const radius = (brushSize * dpr) / 2

      displayCtx.save()
      displayCtx.setTransform(1, 0, 0, 1, 0, 0)
      displayCtx.globalCompositeOperation = composite
      if (showMaskOnly) {
        displayCtx.fillStyle = isEraser ? 'rgba(255,255,255,1)' : '#FFFFFF'
      } else {
        displayCtx.fillStyle = isEraser ? 'rgba(255,255,255,1)' : `rgba(0, 255, 50, ${maskOpacity})`
      }
      displayCtx.beginPath()
      displayCtx.arc(pt.x, pt.y, radius, 0, Math.PI * 2)
      displayCtx.fill()
      displayCtx.restore()

      const maskPt = displayToMask(pt.x, pt.y)
      const maskRadius = getMaskBrushSize() / 2

      maskCtx.save()
      maskCtx.globalCompositeOperation = composite
      maskCtx.fillStyle = isEraser ? 'rgba(255,255,255,1)' : MASK_EXPORT_COLOR
      maskCtx.beginPath()
      maskCtx.arc(maskPt.x, maskPt.y, maskRadius, 0, Math.PI * 2)
      maskCtx.fill()
      maskCtx.restore()
    },
    [activeTool, brushSize, maskOpacity, showMaskOnly, displayToMask, getMaskBrushSize],
  )

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingTool) return
      e.currentTarget.setPointerCapture(e.pointerId)
      isDrawingRef.current = true
      const pt = getCanvasPoint(e)
      if (!pt) return
      lastPointRef.current = pt
      drawDot(pt)
    },
    [isDrawingTool, getCanvasPoint, drawDot],
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const cssPt = getCSSPoint(e)
      if (cssPt) setCursorPos(cssPt)

      if (!isDrawingRef.current || !isDrawingTool) return
      const pt = getCanvasPoint(e)
      if (!pt) return
      const last = lastPointRef.current
      if (last) drawStroke(last, pt)
      lastPointRef.current = pt
    },
    [isDrawingTool, getCSSPoint, getCanvasPoint, drawStroke],
  )

  const handlePointerUp = useCallback(() => {
    if (isDrawingRef.current) {
      isDrawingRef.current = false
      lastPointRef.current = null
      pushSnapshot()
      scheduleMaskExport()
    }
  }, [pushSnapshot, scheduleMaskExport])

  // ---------- clear all ----------------------------------------------------
  const handleClearAll = useCallback(() => {
    const mask = maskCanvasRef.current
    if (!mask) return
    const ctx = mask.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, mask.width, mask.height)
    pushSnapshot()
    redrawDisplay()
    onMaskExport?.('')
  }, [pushSnapshot, redrawDisplay, onMaskExport])

  // ---------- file upload --------------------------------------------------
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) onFileUpload?.(file)
    },
    [onFileUpload],
  )

  // ---------- cursor CSS size ----------------------------------------------
  // brushSize is in CSS pixels, cursor div is in CSS pixels — they match directly
  const cursorCSSSize = brushSize

  // ---------- render -------------------------------------------------------
  return (
    <NodeContainer
      nodeId={id}
      title={name}
      icon="paintbrush"
      selected={selected}
      nodeType={nodeType}
      width={width}
      onSelect={onSelect}
      onHoverChange={onHoverChange}
      onStateChange={onStateChange}
      state={state}
      className={className}
    >
      <CardContent className="relative flex-1 pt-2 pr-2 pb-1 pl-2">
        {/* Image area with canvas overlay */}
        <div
          ref={containerRef}
          className={cn(
            'relative w-full overflow-hidden rounded-md bg-muted',
            imageSrc && isDrawingTool && 'nodrag nopan',
          )}
          style={{ minHeight: 240 }}
        >
          {imageSrc ? (
            <img
              src={imageSrc}
              alt="Mask source"
              className={cn(
                'w-full h-auto select-none pointer-events-none',
                showMaskOnly && 'invisible',
              )}
              draggable={false}
              style={
                sourceImageDims
                  ? { aspectRatio: `${sourceImageDims.w} / ${sourceImageDims.h}` }
                  : undefined
              }
            />
          ) : (
            <label
              htmlFor={`mask-upload-${id}`}
              className="flex flex-col items-center justify-center gap-2 w-full cursor-pointer hover:bg-muted/80 transition-colors"
              style={{ minHeight: 240 }}
            >
              <Icon name="upload" className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Upload or connect an image</span>
              <input
                id={`mask-upload-${id}`}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
          )}

          {/* Display canvas overlay */}
          <canvas
            ref={displayCanvasRef}
            className={cn(
              'absolute inset-0 w-full h-full',
              isDrawingTool ? 'cursor-none' : 'cursor-default pointer-events-none',
              !imageSrc && 'pointer-events-none',
            )}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={() => {
              handlePointerUp()
              setCursorPos(null)
            }}
          />

          {/* Cursor circle preview */}
          {cursorPos && isDrawingTool && imageSrc && (
            <div
              className="pointer-events-none absolute rounded-full"
              style={{
                width: cursorCSSSize,
                height: cursorCSSSize,
                transform: `translate(${cursorPos.x - cursorCSSSize / 2}px, ${cursorPos.y - cursorCSSSize / 2}px)`,
                border: activeTool === 'eraser' ? '1.5px dashed white' : '1.5px solid white',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
                top: 0,
                left: 0,
              }}
            />
          )}
        </div>

        {/* Controls row 1: Brush size slider + Opacity slider */}
        {imageSrc && (
          <div className="flex items-center gap-3 pt-2 px-1">
            <div className="flex items-center gap-2 flex-1">
              <Icon name="circle" className="h-3 w-3 text-muted-foreground shrink-0" />
              <input
                type="range"
                min={MIN_BRUSH_SIZE}
                max={MAX_BRUSH_SIZE}
                value={brushSize}
                onChange={e => setBrushSize(Number(e.target.value))}
                className="nodrag nopan w-full h-1 accent-foreground cursor-pointer"
              />
              <span className="text-[10px] text-muted-foreground tabular-nums w-6 text-right shrink-0">
                {brushSize}
              </span>
            </div>

            <div className="flex items-center gap-2 flex-1">
              <Icon name="blend" className="h-3 w-3 text-muted-foreground shrink-0" />
              <input
                type="range"
                min={10}
                max={100}
                value={Math.round(maskOpacity * 100)}
                onChange={e => setMaskOpacity(Number(e.target.value) / 100)}
                className="nodrag nopan w-full h-1 accent-foreground cursor-pointer"
              />
              <span className="text-[10px] text-muted-foreground tabular-nums w-7 text-right shrink-0">
                {Math.round(maskOpacity * 100)}%
              </span>
            </div>
          </div>
        )}

        {/* Controls row 2: Toolbar buttons */}
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center gap-2 pt-2 pb-1">
            {/* Undo / Redo */}
            <div className="bg-background border border-border rounded-[10px] flex items-center gap-0.5 p-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 rounded-md"
                    onClick={handleUndo}
                    disabled={!canUndo}
                  >
                    <Icon name="undo" className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" size="sm">Undo</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 rounded-md"
                    onClick={handleRedo}
                    disabled={!canRedo}
                  >
                    <Icon name="redo" className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" size="sm">Redo</TooltipContent>
              </Tooltip>
            </div>

            {/* Tool picker — click active tool to deselect */}
            <div className="bg-background border border-border rounded-[10px] flex items-center gap-0.5 p-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant={activeTool === 'brush' ? 'default' : 'ghost'}
                    className={cn('h-7 w-7 rounded-md', activeTool === 'brush' && 'bg-foreground')}
                    onClick={() => toggleTool('brush')}
                  >
                    <Icon
                      name="paintbrush"
                      className={cn('h-4 w-4', activeTool === 'brush' && 'text-background')}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" size="sm">Brush</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant={activeTool === 'eraser' ? 'default' : 'ghost'}
                    className={cn('h-7 w-7 rounded-md', activeTool === 'eraser' && 'bg-foreground')}
                    onClick={() => toggleTool('eraser')}
                  >
                    <Icon
                      name="eraser"
                      className={cn('h-4 w-4', activeTool === 'eraser' && 'text-background')}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" size="sm">Eraser</TooltipContent>
              </Tooltip>
            </div>

            {/* Mask-only toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant={showMaskOnly ? 'default' : 'ghost'}
                  className={cn('h-8 w-8 rounded-[8px]', showMaskOnly && 'bg-foreground')}
                  onClick={() => setShowMaskOnly(prev => !prev)}
                  disabled={!imageSrc}
                >
                  <Icon
                    name="eye"
                    className={cn('h-4 w-4', showMaskOnly && 'text-background')}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" size="sm">Show mask only</TooltipContent>
            </Tooltip>

            {/* Clear all */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 rounded-[8px]"
                  onClick={handleClearAll}
                  disabled={!imageSrc}
                >
                  <Icon name="trash-2" className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" size="sm">Clear mask</TooltipContent>
            </Tooltip>

            <div className="flex-1" />

            {/* Upload */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 rounded-[8px]"
                  onClick={() => document.getElementById(`mask-upload-${id}`)?.click()}
                >
                  <Icon name="upload" className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" size="sm">Upload image</TooltipContent>
            </Tooltip>

            {/* Generate */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="icon" variant="default" className="h-8 w-8 rounded-[8px]" onClick={onGenerate}>
                  <Icon name="sparkles" className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" size="sm">Generate</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </CardContent>
    </NodeContainer>
  )
}

export const MaskNode = withNodeOptimization(MaskNodeComponent, {
  compareProps: ['id', 'selected', 'state', 'imageSrc'],
})
